/**
 * Every URL in the SPA, spelled once (spec §11, §12.5).
 *
 * §12.5: the host serves `/api/v1/*` from Express and everything else as static
 * SPA files with a `try_files ... /index.html` fallback — so any path below
 * survives a refresh and can be bookmarked or sent over WhatsApp.
 *
 * Editors are `/new` or `/:id`, one component either way. The alternative — a
 * separate "create" screen — is how a create form and an edit form drift apart
 * until one of them is missing a field.
 *
 * NOTE ON INVOICES. There is no `invoices/:id/edit`. An issued GST invoice is
 * not amendable and `invoice.edit` does not exist as a capability for anyone
 * (§2.1); a DRAFT invoice is edited through the same `/invoices/:id` editor,
 * which the API gates with `invoice.edit_draft`.
 */
export const routes = {
  /* --- unauthenticated --- */
  login: "/login",
  /** §1.1 — the son's email + password path. Secondary, linked from login. */
  adminLogin: "/admin/login",

  /**
   * ════════════════════════════════════════════════════════════════════════
   * §17.1's CUSTOMER APPROVAL PORTAL — the ONLY path in this file with no
   * session behind it, and the only one a person outside this business ever
   * opens.
   *
   * `/q` IS TWO CHARACTERS ON PURPOSE. It is typed by hand off a printed sheet
   * when a phone camera will not focus, and every character of it is a module in
   * the QR printed at the bottom right of the quotation.
   *
   * THE TOKEN IS NOT PART OF THIS PATH. It travels in the URL FRAGMENT —
   * `/q#<token>` — which no browser puts on a request line, so it reaches no
   * nginx access log on the VPS and no `Referer` header. That is why this is a
   * bare string and not a builder taking a token: a builder would be an
   * invitation to put the credential in the path. The link is composed by the
   * SERVER (`approvalPortalUrl` in the API), from `PUBLIC_APP_BASE_URL`, so it
   * can never be built from a request header — see that function for the
   * phishing case that rules out.
   * ════════════════════════════════════════════════════════════════════════
   */
  approvalPortal: "/q",

  /* --- the app (all behind ProtectedRoute) --- */
  /** §11.2 — "the dashboard IS the home screen". */
  home: "/",

  quotations: "/quotations",
  quotationNew: "/quotations/new",
  quotation: (id: number | string) => `/quotations/${id}`,
  /**
   * §11.2 — "Quotations waiting for reply: 3" opens the quotation list filtered
   * to SENT. The parameter is named `status` because that is what
   * `GET /quotations?status=` is called (§7): one name for the filter from the
   * home screen through the list screen to the API is one fewer thing to
   * translate at each boundary.
   */
  quotationsByStatus: (status: string) => `/quotations?status=${encodeURIComponent(status)}`,
  /**
   * §11.3 — "Make a copy". A query parameter rather than a path segment,
   * because the destination IS the new-quotation editor; only its initial data
   * differs. Never copies the number, the dates or the S.O./Challan numbers.
   */
  quotationCopy: (id: number | string) => `/quotations/new?copyFrom=${id}`,
  /**
   * REVISING AN OFFER THAT HAS NOT BEEN ACCEPTED YET (§7) — a real route, and
   * the thing an invoice deliberately has no counterpart to.
   *
   * The server allows a `PUT` only while the quotation is DRAFT or SENT, and
   * answers `isEditable` so this link can be hidden the rest of the time. It is
   * hidden, never disabled: an APPROVED quotation is the record of an offer the
   * customer accepted, and a greyed-out "Edit" invites the question of how to
   * un-grey it. `quotationCopy` above is the honest answer in that case — a new
   * offer with its own number, leaving the accepted one intact.
   */
  quotationEdit: (id: number | string) => `/quotations/${id}/edit`,
  /**
   * §11.1's after-save screen for a quotation: Share on WhatsApp, Download PDF,
   * Make this a bill. A real route rather than a modal, because §11.1 calls it
   * "one screen" and because a saved document must survive a refresh, a back
   * button and a link the owner sent himself.
   */
  quotationShare: (id: number | string) => `/quotations/${id}/share`,

  invoices: "/invoices",
  invoiceNew: "/invoices/new",
  invoice: (id: number | string) => `/invoices/${id}`,
  invoiceCopy: (id: number | string) => `/invoices/new?copyFrom=${id}`,
  /**
   * §11.2 — "This month: 12 bills, ₹1,45,600" opens the bills of that month,
   * and every figure on Reports opens the bills behind it. "Every figure is
   * tappable and leads somewhere useful. A number with no destination is
   * decoration."
   *
   * `from` / `to` are INCLUSIVE calendar days, `YYYY-MM-DD` — the same names
   * and the same meaning as `GET /invoices?from=&to=` (§7), and the same pair
   * `GET /dashboard/summary` already answers with, so the home screen hands
   * them straight through without reinterpreting them.
   */
  invoicesInPeriod: (from: string, to: string) =>
    `/invoices?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
  /** The Reports customer-wise rows: one customer's bills in the chosen period. */
  invoicesForCustomerInPeriod: (customerId: number | string, from: string, to: string) =>
    `/invoices?customerId=${encodeURIComponent(String(customerId))}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
  /** §7.1 — one-tap conversion; lands in the invoice editor pre-filled. */
  invoiceFromQuotation: (quotationId: number | string) =>
    `/invoices/new?fromQuotation=${quotationId}`,
  /**
   * §17.5 — the register showing only bills with something still owed, and what
   * the home screen's "who owes me money" card opens.
   *
   * ONE PARAMETER AND NOTHING ELSE, deliberately. The unpaid list is answered by
   * `GET /payments/outstanding`, which takes no query parameters at all: an
   * outstanding figure is `grand_total_paise` − `SUM(payments.amount_paise)`,
   * which the invoice register's `where` cannot express. So this filter is
   * EXCLUSIVE — the register drops its other filters while it is on, rather than
   * leaving a date range visibly set and silently ignored.
   *
   * The name matches the query key the register reads and the one word the
   * owner sees on the control, which is what stops "unpaid", "outstanding" and
   * "owed" becoming three names for one thing between the card and the SQL.
   */
  invoicesUnpaid: "/invoices?unpaid=true",
  /** §11.1's after-save screen for a bill. */
  invoiceShare: (id: number | string) => `/invoices/${id}/share`,
  /**
   * §6.1 — "Record a bill I already wrote". A separate path, not a mode flag on
   * the editor, because the number is ENTERED here and allocated there, and
   * those two are different enough that one screen doing both would be one
   * checkbox away from typing a number onto an app-numbered invoice.
   */
  invoiceRecordManual: "/invoices/record",
  /**
   * §6.1's counter override — "the escape hatch, not the main road".
   *
   * ITS OWN ROUTE, GATED ON `invoice.set_counter`, BECAUSE THE OWNER HOLDS THAT
   * CAPABILITY AND NOT `settings.write`. §2.1 grants it to him by name and §6.1
   * argues at length that it must not be folded into `settings.write` (which
   * also governs the GSTIN, the PAN and the bank details). Living only inside
   * the `settings.write`-gated Settings screen made it unreachable for the one
   * person who writes bills by hand — the person it is for.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   * MOVED FROM `/invoices/numbers` TO `/settings/numbers` ON 2026-08-17.
   *
   * It lived under `/invoices/` on the argument that this is "what it is to him:
   * the number the next bill will get" — while the CORRECTNESS half of the
   * reasoning above was that it must not sit behind `settings.write`, which the
   * owner does not hold.
   *
   * That correctness half is now answered somewhere better. `SettingsLayout` is
   * UNGATED and this route keeps its own `invoice.set_counter` guard, so the URL
   * no longer carries the gate — the route does. What the old path bought was
   * therefore nothing, and what it cost was visible: the site header
   * prefix-matches `/invoices` and printed "Bills" over a screen sitting inside
   * the Settings menu, with a Settings item highlighted beside it.
   *
   * It also DROPS a constraint rather than adding one. Under `/invoices/` this
   * had to be declared before `invoices/:id` or the `:id` pattern swallowed the
   * word "numbers". `/settings/` has no `:id` sibling, so that ordering hazard
   * is gone — see the old note in capability-reachability.test.tsx.
   *
   * The constant keeps its name: it is still the invoice counter to every caller,
   * and renaming it would touch a dozen files to change nothing.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  invoiceNumbers: "/settings/numbers",

  /**
   * Delivery challans (§17.7) — the document goods travel under when there is
   * NO tax invoice. Behind the `challans` flag (§18), so a disabled feature is
   * a 404 on the API and a hidden nav item here.
   *
   * The same family as quotations: a register, an editor for new and for
   * revising, and a detail screen. There is deliberately no `challanCopy` —
   * every movement is its own event with its own vehicle and its own date, and
   * a copied challan would carry last week's lorry number onto today's gate.
   */
  challans: "/challans",
  challanNew: "/challans/new",
  challan: (id: number | string) => `/challans/${id}`,
  /**
   * Editable only while the goods are still out, which the SERVER decides on the
   * stored status. As with `quotationEdit`, the button is HIDDEN rather than
   * disabled once it closes: a returned or billed challan is a record of a
   * movement that happened, and greying a control invites hunting for the way to
   * un-grey it.
   */
  challanEdit: (id: number | string) => `/challans/${id}/edit`,
  /**
   * §17.7's payoff — the register filtered to deliveries waiting to be billed.
   * The query name matches the API's `openOnly`, one name for the filter from
   * the home screen to the SQL.
   */
  challansUnbilled: "/challans?openOnly=true",

  /**
   * Credit notes (§17.3, section 34(1) CGST) — the document that REDUCES a tax
   * invoice already issued. Behind the `credit_notes` flag (§18), which ships
   * disabled, so the API answers 404 and the register renders "not switched on".
   *
   * ═══════════════════════════════════════════════════════════════════════════
   * THE PATHS ARE NOT THE OTHER THREE DOCUMENTS' PATHS, AND EACH DIFFERENCE IS
   * A RULE RATHER THAN A STYLE CHOICE.
   *
   * THERE IS NO `creditNoteNew`. A credit note is RAISED FROM THE BILL IT
   * CORRECTS — §34 requires the link, and a blank "new credit note" form asking
   * which invoice is an invitation to pick the wrong one, on the one document
   * whose entire job is to name another. So the editor is reached only as
   * {@link creditNoteFor}, and the invoice id is in the PATH rather than a query
   * parameter, because without it the screen has nothing to open at all.
   *
   * THERE IS NO `creditNoteEdit`. `creditnote.edit` does not exist as a
   * capability for anyone (§2.1) — the same reasoning as `invoice.edit`, and not
   * creating it is stronger than granting it to nobody. A wrong credit note is
   * cancelled and a fresh one raised.
   *
   * THERE IS NO `creditNoteCopy` EITHER. Copying is §11.3's highest-value
   * shortcut for a BILL, where the same few jobs repeat every week. Nothing
   * about a correction repeats: a copied credit note would carry last month's
   * reason onto a different invoice, which is the exact mistake §34 asks the
   * reason field to prevent.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  creditNotes: "/credit-notes",
  creditNote: (id: number | string) => `/credit-notes/${id}`,
  /**
   * The editor, opened from ONE bill and carrying its id (§17.3 rule 1).
   *
   * `/invoices/:invoiceId/credit-note` rather than `/credit-notes/new?invoiceId=`
   * because the invoice is not an option on this form, it is the subject of it:
   * a path that cannot be reached without naming a bill cannot be reached with
   * the wrong one blank. The screen refuses to open without it.
   */
  creditNoteFor: (invoiceId: number | string) => `/invoices/${invoiceId}/credit-note`,
  /**
   * The register narrowed to the notes against one bill — what the "credited"
   * figure on the invoice opens (§11.2: "a number with no destination is
   * decoration").
   *
   * The parameter is named `invoiceId` because that is what
   * `GET /credit-notes?invoiceId=` is called, which is also the filter the card
   * under the bill reads. One name from the figure through the register to the
   * SQL is one fewer thing to translate at each boundary.
   */
  creditNotesForInvoice: (invoiceId: number | string) =>
    `/credit-notes?invoiceId=${encodeURIComponent(String(invoiceId))}`,

  customers: "/customers",
  customerNew: "/customers/new",
  customer: (id: number | string) => `/customers/${id}`,
  /**
   * §17.5 — what this customer owes, and the bills behind it.
   *
   * A SIBLING OF THE CUSTOMER EDITOR, NOT A REPLACEMENT FOR IT. `/customers/:id`
   * is the RECORD — name, GSTIN, address, the fields that print on the next bill
   * — and it is gated on `customer.view`. This is a money question, gated on
   * `payment.view`, which is a different capability held by a different set of
   * people (the ACCOUNTANT holds this one and no editing capability at all).
   * Folding the statement into the editor would put a payments-flagged section
   * inside a screen that must keep working when the module is off.
   */
  customerStatement: (id: number | string) => `/customers/${id}/statement`,

  /** §11.1 calls this the rate card; the OWNER sees "My list" (§11.4 strings). */
  items: "/items",
  itemNew: "/items/new",
  item: (id: number | string) => `/items/${id}`,

  /**
   * The notification hub — what the bell in the header opens.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   * A REAL PATH, NOT A POPOVER, AND NOT A NAV ITEM EITHER.
   *
   * A PATH because a notification's whole job is to be acted on: the reader taps
   * a compliance alert, opens the bill, records the number, and presses back —
   * which has to return them to the list rather than to whatever screen they
   * happened to be on when they opened a dropdown. A popover cannot survive that
   * round trip, nor a refresh, nor a link sent from one phone to the other.
   *
   * NO SIDEBAR ITEM. The nav is the handful of things the owner touches daily
   * (§11.2) and an inbox is not one of them — it is somewhere you go BECAUSE the
   * bell told you to, which is exactly what the badge is for. A permanent nav row
   * reading "Notifications: 0" is the thing the eye learns to skip, and then the
   * day it says 3 it gets skipped too.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  notifications: "/notifications",

  /**
   * The shared conversation — what the speech bubble in the header opens.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   * `/messages` IS A LIST, NOT A CONVERSATION (2026-08-19). It shows the people
   * you can write to privately and, BELOW THEM, the shared group chat:
   *
   *     Dad
   *     Accountant
   *     ─────────────
   *     Our group chat
   *
   * WHAT CHANGED AND WHY. Until now the group chat WAS `/messages` and the
   * private conversations were a separate screen at `/messages/direct`, reached
   * by a button in the room's own header. That made the shared room the front
   * door and every person behind it — so "write to my accountant" was two
   * navigations, and the owner had to know that a screen called Messages was in
   * fact one particular conversation. The owner asked for the opposite ordering
   * in as many words: "so for eg if I've logged in I'll see my dad my accountant
   * and our group chat". People first; the group is a row like any other.
   *
   * THE GROUP CHAT IS NOT DELETED — only its status as a separate PLACE is. It
   * keeps its own route, its own read model (one watermark per person, against
   * the pair-wise watermark a private conversation keeps) and its own
   * capability; it simply opens in the same pane, drawn by the same components.
   *
   * A PATH for the same reason the hub is one: the reader taps a message about a
   * bill, opens the bill, and presses back — which has to return them to the
   * conversation rather than to whatever screen they were on when a dropdown
   * opened. A popover survives none of that, nor a refresh, nor a link sent from
   * one phone to the other.
   *
   * A SEPARATE PATH FROM THE HUB, because the two screens are different objects
   * with different read models: an inbox of rows each with ITS OWN read state,
   * and one conversation with a single watermark per person. Folding them into
   * tabs behind `/notifications` would give one URL two meanings and would make
   * "mark all read" ambiguous about which of the two it cleared.
   *
   * NO SIDEBAR ITEM, exactly as the hub has none. The nav is the handful of
   * things the owner touches daily (§11.2); a conversation is somewhere you go
   * because the badge told you to. A permanent row reading "Messages: 0" is what
   * the eye learns to skip, and then the day it says 2 it gets skipped too.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  messages: "/messages",

  /**
   * The shared room — everybody, on the record.
   *
   * `group` IS A LITERAL SEGMENT AND IT SITS BESIDE `:userId` BELOW. That is
   * unambiguous twice over: React Router ranks a static segment above a dynamic
   * one whatever the declaration order, and `parsePeerId` accepts nothing but a
   * string of digits, so "group" could never be read as a person. It is still
   * DECLARED FIRST in routes/index.tsx, and said out loud there, because the
   * next literal segment somebody adds under `/messages` may not be so lucky.
   *
   * `/messages/direct` AND `/messages/direct/:id` ARE GONE — not aliased and not
   * redirected. The private conversations are rows on `/messages` now and each
   * one is `/messages/:userId`. A segment that is neither a person nor "group"
   * is not a route at all: the screen sends it Home, exactly as the catch-all
   * does for every other unrecognised URL.
   */
  messagesGroup: "/messages/group",
  /**
   * ONE private conversation, named by THE OTHER PERSON rather than by a
   * conversation id — the API has no such id, and inventing one on the client
   * would be a second name for something the pair already names exactly.
   *
   * The peer sits in the URL rather than in component state so a conversation
   * survives a refresh and the back button walks between people, which is what
   * makes the list beside it navigation rather than a filter.
   */
  messagesWith: (userId: number | string) => `/messages/${userId}`,

  /**
   * §17A.1 — THE MAILBOX. What the business has emailed out, and eventually what
   * came back.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   * IT HAS A RAIL ROW, WHERE `notifications` AND `messages` ABOVE DELIBERATELY DO
   * NOT, AND THE DIFFERENCE IS NOT INCONSISTENCY.
   *
   * Those two are things you are SENT TO: a badge lights up, you go, you clear
   * it. A permanent row reading "Notifications: 0" is what the eye learns to
   * skip. This is a PLACE YOU LOOK THINGS UP IN — "did that quotation actually
   * go, and to which address?" is a question asked cold, weeks later, with
   * nothing on screen prompting it. A destination nobody can be prompted towards
   * needs a door, or it does not exist. That is the same argument `creditNotes`
   * makes for being in the rail while being created from somewhere else.
   *
   * THE FOLDER IS A QUERY PARAMETER, NOT A PATH SEGMENT. `/mail?folder=SENT`
   * rather than `/mail/sent`, because the folder is a FILTER over one register —
   * exactly what `invoicesUnpaid` and `challansUnbilled` are — and the register
   * is the screen. It still survives a refresh, a back button and a link sent
   * from one phone to the other, which is the whole reason it is not component
   * state. The name matches the API's `?folder=` and the `MailFolder` enum in
   * `schema.prisma`, so one word travels from the control to the SQL.
   *
   * THERE IS NO `/mail/:id` AND NO COMPOSE PATH. A message's body is short and
   * is shown in the list, so a detail screen would be a second URL for a row
   * that has nothing more to say; and nothing in this app composes free mail —
   * sending is an action ON A DOCUMENT, from that document's own screen (§17A.2
   * stage 1), which is what stops the mailbox becoming an open relay with a
   * "To:" field.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  mail: "/mail",
  /**
   * One folder of it. `INBOX`, `DRAFTS`, `SENT`, `SPAM`, `TRASH` — the
   * `MailFolder` values, uppercase, exactly as the enum spells them, because a
   * lowercased URL word would need a translation table at both ends.
   */
  mailFolder: (folder: string) => `/mail?folder=${encodeURIComponent(folder)}`,

  /**
   * §11.2's secondary screen — "period selector … plus a customer-wise summary
   * and an export button … This screen exists mainly to feed the CA."
   *
   * Reached from a SMALL tile on Home, not from the nav bar: the nav is the
   * four things the owner touches daily, and a monthly errand for the
   * accountant is not one of them.
   */
  reports: "/reports",

  /**
   * The company form — still `settings.write`, and still ADMIN-only (§2.1).
   *
   * IT IS ALSO THE DOOR TO THE SETTINGS AREA, which is why the route is no
   * longer wrapped in a bare `settings.write` guard. One rail row now leads here
   * for anyone holding `settings.write`, `user.manage` OR `audit.view`, and
   * `SettingsIndexRoute` (routes/index.tsx) renders the company form for the
   * first of those and redirects the others to the first section they can open —
   * People, or the activity log. Nothing widened: the GSTIN, the PAN and the
   * bank details are behind the same capability they always were, and a reader
   * without it never renders `SettingsScreen` at all.
   */
  settings: "/settings",

  /**
   * §2, §13 — WHO HAS AN ACCOUNT, gated on `user.manage`.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   * ITS OWN ROUTE, AND ITS OWN CAPABILITY, FOR THE REASON `invoiceNumbers`
   * RECORDS ONE SEGMENT ABOVE. `/settings` is guarded by `settings.write`;
   * `user.manage` is a DIFFERENT capability in §2.1's Administration row, and
   * the two are the same set of people only by today's accident of the map.
   * Rendering this as a card inside `SettingsScreen` would bind "may I create an
   * account" to "may I change the bank details" — exactly the collapse that once
   * put §6.1's counter override behind a door the OWNER cannot open. A future
   * role granted `user.manage` and not `settings.write` reaches this screen and
   * nothing else moves.
   *
   * UNDER `/settings/` RATHER THAN `/admin/` because that is where the reader
   * already is: it is administration of the same system Settings configures, and
   * the rail groups the three together at its foot. The segment is static, so
   * nothing dynamic can swallow it.
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * THERE IS NO `/settings/users/:id` AND NO `/settings/users/new`. Three rows
   * are edited in place on the list, and the API has no screen-shaped detail to
   * open — a path per account would be three URLs describing one table. There is
   * no delete path either, on this route or anywhere: an account is deactivated,
   * never removed (§2, and the schema's `RESTRICT`/`SetNull` keys).
   */
  users: "/settings/users",

  /**
   * §13, §14.3 — the audit window, gated on `audit.view`.
   *
   * The owner asked for it in as many words: "a full audit window for me — who
   * logged in when, when was what happened, and this will stay indefinitely."
   * `audit_log` has been written by every mutating path since the beginning and
   * retained 72 months; until this route nothing could read a row of it.
   *
   * ADMIN-ONLY AND MEANT TO STAY SO. `audit.view` is textually a `*.view`
   * capability and is deliberately NOT in the shared read row (§2.1 lists it on
   * the ADMIN row, and the more specific row wins) — the trail holds every
   * refused attempt, every lockout and the before-image of every settings
   * change. The OWNER is refused it and so is the CA.
   *
   * A SIBLING OF `users`, NOT A TAB INSIDE IT. They answer different questions
   * for the same reader — "who may do what" and "what was done" — and folding
   * them into one URL would make a link to either ambiguous. The FILTERS live in
   * the query string, so a narrowed window ("everything the CA did last March")
   * survives a reload and can be sent from one phone to the other.
   */
  audit: "/settings/audit",

  /**
   * §18.1 — THE FEATURE FLAG SCREEN, gated on `settings.write`.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   * UNTIL THIS LANDED, SWITCHING A FEATURE ON WAS AN SSH SESSION AND A
   * HAND-WRITTEN `UPDATE` AGAINST A PRODUCTION TAX DATABASE. Both halves of the
   * API shipped long ago — `GET /flags` and `PUT /flags/:key`, audited, cached
   * and invalidated on commit — with nothing in front of either, and the owner
   * has four switches to throw (credit notes, messages, notifications, the CA's
   * login). Four SQL statements typed by hand next to `invoices` and
   * `feature_flags` in the same shell is how the wrong table gets the `WHERE`.
   *
   * `settings.write` AND NOT A CAPABILITY OF ITS OWN, because that is exactly
   * what the API asks for — the whole `/flags` router sits behind it, INCLUDING
   * the read, since the list names every unreleased module in the build. This is
   * therefore NOT the `invoiceNumbers` mistake in another costume: the route
   * names the capability its own endpoint enforces, rather than a neighbouring
   * one that happens to resolve to the same person today.
   *
   * UNDER `/settings/` LIKE `users` AND `audit`, and a sibling of both rather
   * than a card inside the company form: "which parts of the app are on" is a
   * different question from "what is my GSTIN", and folding it into
   * `SettingsScreen` would bury a switch that takes billing down inside a page
   * people scroll past to reach the bank details.
   *
   * THE SCREEN LIVES AT `features/settings/FeaturesScreen.tsx`. That location is
   * load-bearing: `components/__tests__/feature-off-copy.test.ts` looks for a
   * `Feature*`-named component there before it will allow `flags.moduleOffHelp`
   * to name this screen at all.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  features: "/settings/features",
  /*
   * THERE IS NO `profile` PATH, AND THAT IS THE POINT — do not add one back.
   *
   * The signed-in person's own preferences (language, text size, theme — §11.1,
   * §11.4) are a SHEET opened in place, `features/preferences/ProfileSheet.tsx`,
   * because he is mid-way through a bill when he decides the type is too small
   * and a page would navigate him away and back. It is deliberately outside the
   * `settings.write` gate: that capability is ADMIN-only and covers the GSTIN,
   * the bank details and the tax master, while the OWNER — who holds none of it
   * — is exactly the person who needs bigger type and a Marathi interface.
   *
   * `profile: "/profile"` used to sit here and was never mounted in
   * `routes/index.tsx`. Its ONE consumer, the search palette's "My settings"
   * row, therefore navigated into the catch-all and landed on the dashboard with
   * no sheet and no explanation. It opens the sheet now, through
   * `PreferencesSheetProvider`. A path declared here is a promise that the route
   * table keeps; this one could not be kept, so it is gone rather than pointing
   * nowhere.
   */
} as const;

/**
 * Route PATTERNS for `<Route path>`. Kept beside the builders above so a
 * renamed segment is one edit, not two that can disagree.
 */
export const patterns = {
  login: "login",
  adminLogin: "admin/login",
  /** §17.1's portal. Outside `ProtectedRoute` and outside `AppShell` — see routes/index.tsx. */
  approvalPortal: "q",
  home: "/",
  quotations: "quotations",
  quotationNew: "quotations/new",
  quotation: "quotations/:id",
  /** §7's revise-an-offer editor. Literal segment, so it outranks `:id`. */
  quotationEdit: "quotations/:id/edit",
  /** §11.1's after-save screen. Declared beside `:id`; the literal segment wins. */
  quotationShare: "quotations/:id/share",
  invoices: "invoices",
  invoiceNew: "invoices/new",
  /**
   * §6.1. Declared BEFORE `invoices/:id` below. React Router ranks a static
   * segment above a dynamic one regardless of order, but the ordering here says
   * so out loud — the same reason `invoices/new` sits where it does.
   */
  invoiceRecordManual: "invoices/record",
  /*
   * §6.1's counter override, now a SETTINGS section — see `routes.invoiceNumbers`
   * for why it moved. It no longer needs to outrank `invoices/:id`, because it is
   * no longer that pattern's sibling.
   */
  invoiceNumbers: "settings/numbers",
  invoice: "invoices/:id",
  invoiceShare: "invoices/:id/share",
  /** §17.7 — delivery challans. `new` is static, so it outranks `:id` below. */
  challans: "challans",
  challanNew: "challans/new",
  challan: "challans/:id",
  challanEdit: "challans/:id/edit",
  /**
   * §17.3 — credit notes. Two patterns for three paths: the register carries its
   * narrowing in a query string, which is not part of the pattern.
   *
   * `creditNoteFor` LIVES UNDER `invoices/:id`, not under `credit-notes`, and it
   * has MORE SEGMENTS than `invoices/:id` — so React Router ranks it higher and
   * the bill's detail screen cannot swallow it. Same shape and same reason as
   * `customers/:id/statement`.
   */
  creditNotes: "credit-notes",
  creditNote: "credit-notes/:id",
  creditNoteFor: "invoices/:id/credit-note",
  customers: "customers",
  customerNew: "customers/new",
  customer: "customers/:id",
  /**
   * §17.5's statement. More segments than `customers/:id`, so React Router ranks
   * it higher and the editor cannot swallow it — the ordering here is for the
   * reader, as elsewhere in this file.
   */
  customerStatement: "customers/:id/statement",
  items: "items",
  itemNew: "items/new",
  item: "items/:id",
  /** The hub. One flat path — a notification's detail IS the document it opens. */
  notifications: "notifications",
  /**
   * THE MESSENGER — THREE PATTERNS, ONE SCREEN.
   *
   * `messages` is the list with nothing open; `messages/group` is the list with
   * the shared room open beside it; `messages/:userId` is the list with one
   * person open beside it. They are the same surface in three states, which is
   * why they are one component: a list you must LEAVE to read a conversation
   * would put a navigation between "the CA is waiting" and answering her.
   *
   * `messages/group` IS DECLARED FIRST, and routes/index.tsx says why. Nothing
   * depends on that order — a static segment outranks a dynamic one in React
   * Router regardless, and `parsePeerId` takes digits only — but a reader
   * scanning this file should not have to know either fact to be sure that
   * "group" is not somebody's user id.
   */
  messages: "messages",
  messagesGroup: "messages/group",
  messagesWith: "messages/:userId",
  /**
   * §17A.1's mailbox. ONE flat path for five folders, because the folder is a
   * query parameter and a query string is not part of a route pattern — see
   * `routes.mail` for why it is a filter rather than a segment.
   */
  mail: "mail",
  reports: "reports",
  settings: "settings",
  /**
   * The two ADMIN surfaces §2.1 named. Both are STATIC segments under
   * `settings/`, and `settings` itself has no `*` on it, so neither can be
   * swallowed by the company form — the nesting here is for the reader.
   */
  users: "settings/users",
  audit: "settings/audit",
  /** §18.1's flag screen. Static too, so `settings` cannot swallow it either. */
  features: "settings/features",
  /* No `profile` pattern — see the note in `routes` above. */
} as const;
